// LICENSE_CODE ZON
'use strict'; /*jslint react:true*/
import React, {useMemo, useState, useCallback, useEffect} from 'react';
import cn from 'classnames';
import {Input} from 'uikit';
import {Text} from '@pub/comp/text';
import '@pub/css/select.less';

const MANY_ITEMS_THRESHOLD = 100;

const TEXT_SIZE_MAP = {
    s: 'xxs',
    m: 'xs',
    l: 's',
};
const CONTROL_SIZE = {
    s: {
        minHeight: '28px',
        padding: '2px 7px',
    },
    m: {
        minHeight: '32px',
        padding: '4px 7px',
    },
    l: {
        minHeight: '34px',
        padding: '6px 8px',
    },
};

const render_option_default = (value, props)=>{
    const {size='m', is_focused, is_selected} = props;
    const Comp = Text[TEXT_SIZE_MAP[size]];
    const classnames = cn('ss-opt', {
        'ss-opt--focused': is_focused,
        'ss-opt--selected': is_selected,
    });
    return <Comp className={classnames} primary={is_selected}
      bold={is_selected}>
      {value.label}
    </Comp>;
};

const render_value_default = (value, props)=>{
    const {size='m'} = props;
    const Comp = Text[TEXT_SIZE_MAP[size]];
    return <Comp className="ss-val">{value.label}</Comp>;
};

const normalize_val = (value, options)=>{
    if (Array.isArray(value))
        return value;
    return [options?.find(opt=>opt.value==value)].filter(Boolean);
};

function Value_container({children, innerProps, innerRef}){
    return <div className="ss-value-wrap" ref={innerRef} {...innerProps}>
      {children}
    </div>;
}

function Wrapper({
    data,
    innerProps,
    innerRef,
    render,
    size,
    as='div',
    isSelected: is_selected,
    isFocused: is_focused,
    base_class,
})
{
    const Comp = as;
    const classnames = cn(base_class, {
        [`${base_class}--selected`]: is_selected,
        [`${base_class}--focused`]: is_focused,
    });
    return <Comp className={classnames} ref={innerRef} {...innerProps}>
      {render(data, {size, is_selected, is_focused})}
    </Comp>;
}

const get_styles = props=>({
    menu: base=>({...base, zIndex: 2}),
    control: base=>({
        ...base,
        ...CONTROL_SIZE[props.size||'m'],
        borderRadius: '6px',
    }),
    input: base=>({...base, marginTop: '0', marginBottom: '0'}),
    indicatorsContainer: base=>({
        ...base,
        flexShring: 0,
        height: '16px',
        minHeight: '22px',
        marginLeft: '8px',
    }),
});

const normalize_string = str=>String(str?.toLowerCase?.().trim()) || '';

const filter_option = (candidate, input)=>{
    if (!input)
        return true;
    if (candidate.data.__isNew__)
        return true;
    const n = normalize_string;
    const token = n(input);
    let val = n(candidate.label)+n(candidate.value);
    if (candidate.data?.search_value)
        val += n(candidate.data.search_value);
    return val.includes(token);
};

export function Select({
    id,
    className,
    disabled,
    placeholder,
    loading,
    on_change,
    options,
    render_option=render_option_default,
    render_value=render_value_default,
    value,
    multi,
    clearable,
    size,
    auto_focus,
    option_height,
})
{
    const normalized_val = normalize_val(value, options);
    const Option_comp = useMemo(()=>function Opt_inner({...props}){
        return <Wrapper {...props} render={render_option} size={size}
          as="li" base_class="ss-option-view"/>;
    }, [render_option, size]);
    const Value_comp = useMemo(()=>function Value_inner({...props}){
        return <Wrapper {...props} render={render_value} size={size}
          base_class="ss-value-view"/>;
    }, [render_value, size]);
    const comps = useMemo(()=>({
        Option: Option_comp,
        MultiValueLabel: Value_comp,
        ValueContainer: Value_container,
        SingleValue: Value_comp,
    }), [Option_comp, Value_comp]);
    const styles = useMemo(()=>get_styles({size}), [size]);
    const [sorted_opts, set_so] = useState(options);
    useEffect(()=>void set_so(options), [options]);
    const handle_search_change = useCallback(search_str=>{
        if (!search_str)
        {
            if (sorted_opts!==options)
                set_so(options);
            return;
        }
        const sorted = [].concat(options);
        const n = normalize_string;
        const nsearch = n(search_str);
        sorted.sort((a, b)=>{
            const al = n(a.label);
            const bl = n(b.label);
            if (al.startsWith(nsearch) && !bl.startsWith(nsearch))
                return -1;
            const av = n(a.value);
            const bv = n(b.value);
            if (av.startsWith(nsearch) && !bv.startsWith(nsearch))
                return -1;
            return 0;
        });
        set_so(sorted);
    }, [options]);
    const handle_menu_close = useCallback(()=>{
        if (sorted_opts !== options)
            set_so(options);
    }, [sorted_opts, options]);
    return <div className={cn('sdk-select', multi&&'ss--multi', className)}>
      <Input.Dropdown
        id={id}
        multiselect={multi}
        options={sorted_opts}
        disabled={disabled}
        onChange={on_change}
        onMenuClose={handle_menu_close}
        onInputChange={handle_search_change}
        placeholder={placeholder||'Select option'}
        isLoading={loading}
        value={normalized_val}
        styles={styles}
        components={comps}
        clearable={clearable}
        filterOption={filter_option}
        virtualized={options.length>MANY_ITEMS_THRESHOLD}
        autoFocus={auto_focus}
        optionHeight={option_height}
      />
    </div>;
}
